<template>
  <div :style="{ width: '100%' }">
    <div :id="`custom-froala-${elementId}-${id}`" :class="getClassName" v-html="getEditorContent" />
  </div>
</template>

<script>
import FroalaEditor from 'froala-editor'
import { FROALA_CONFIG2, FROALA_DEFAULT_CONTENT } from '@src/constants'
import { getParentDOM } from '@src/helper'
export default {
  props: {
    id: { type: String, default: null, required: true },
    options2: { type: [Object], default: null, required: true },
    sections2: { type: Array, default: null, required: true },
    elementId: { type: String, default: null, required: true },
    defaultHtml: { type: String, default: null },
    live: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
    renderOnly: { type: Boolean, default: false },
    enableImage: { type: Boolean, default: false },
    disableFontSize: { type: Boolean, default: false },
    disableFontFamily: { type: Boolean, default: false },
    disableTextAlign: { type: Boolean, default: false }
  },
  data() {
    return {
      controller: null,
      textEditorId: null,
      textEditorObject: null,
      froalaDefaultContent: FROALA_DEFAULT_CONTENT,
      showEditor: false,
      wrapElement: null
    }
  },
  computed: {
    getEditorContent() {
      if (this.defaultHtml) {
        return this.defaultHtml
      }
      if (!this.live && !this.preview && !this.renderOnly) {
        return this.froalaDefaultContent
      }
      return ''
    },
    getClassName() {
      let className = ['fr-view']
      if (!this.live && !this.preview && !this.renderOnly) {
        className.push('custom-textEditors')
      } else if (!this.defaultHtml) {
        className.push('empty-content')
      }
      return className.join(' ')
    }
  },
  mounted() {
    if (!this.live && !this.preview && !this.renderOnly) {
      this.wrapElement = document.getElementById('ne-editor-toolbar-2')
      this.$nextTick(() => {
        this.initTextEditors()
        document.body.addEventListener('mousedown', this.manageTextEditors)  
      })
    }
  },
  beforeDestroy() {
    if (!this.live && !this.preview && !this.renderOnly) {
      if (this.wrapElement) {
        this.wrapElement.classList.remove('active')
      }
      if (this.textEditorObject) {
        this.textEditorObject.removeEventListener('click', this.initTextEditor)
      }
      document.body.removeEventListener('mousedown', this.manageTextEditors)
      if (this.controller) {
        this.controller.destroy()
        this.controller = null
      }
    }
  },
  methods: {
    callEvent(params) {
      this.$emit('callEvent', params)
    },
    initTextEditors() {
      this.textEditorId = `custom-froala-${this.elementId}-${this.id}`
      this.textEditorObject = document.getElementById(this.textEditorId)
      if (this.textEditorObject) {
        this.textEditorObject.addEventListener('click', this.initTextEditor)
      }
    },
    initTextEditor(e) {
      e.preventDefault()

      this.showEditor = true
      if (this.wrapElement) {
        this.wrapElement.classList.add('active')
      }

      if (!this.controller) {
        // Not Initiated
        if (this.textEditorObject.innerHTML.includes(this.froalaDefaultContent)) {
          this.textEditorObject.innerHTML = ''
        }
        const froalaConfig = JSON.parse(JSON.stringify(FROALA_CONFIG2))
        froalaConfig.imageUpload = this.enableImage

        if (this.enableImage) {
          froalaConfig.toolbarButtons = froalaConfig.toolbarButtonsImage
        } else {
          froalaConfig.toolbarButtons = froalaConfig.toolbarButtonsDefault
        }
        let index
        if (this.disableFontSize) {
          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('fontSize')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)
        }
        if (this.disableFontFamily) {
          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('fontFamily')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)
        }
        if (this.disableTextAlign) {
          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('alignLeft')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)

          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('alignRight')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)

          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('alignCenter')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)

          index = froalaConfig.toolbarButtons.moreText.buttons.indexOf('alignJustify')
          froalaConfig.toolbarButtons.moreText.buttons.splice(index, 1)
        }
        froalaConfig.toolbarButtons.moreText.buttonsVisible = froalaConfig.toolbarButtons.moreText.buttons.length

        this.controller = new FroalaEditor(`#${this.textEditorId}`, {
          ...froalaConfig,
          events: {
            'initialized': () => {
              if (this.textEditorObject.classList) {
                this.textEditorObject.classList.remove('fr-view')
              }
              this.controller.toolbar.show()
              this.controller.events.focus()
            },
            'paste.after': () => {
              const editorEl = this.controller.$el.get(0)
              const elements = editorEl.querySelectorAll('[style]')
              elements.forEach(el => {
                const cleaned = el.getAttribute('style')
                  .replace(/font-family:[^;]+;?/gi, '')
                  .replace(/font-size:[^;]+;?/gi, '')
                  .replace(/font-weight:[^;]+;?/gi, '')
                  .replace(/font-style:[^;]+;?/gi, '')
                  .replace(/font-variant:[^;]+;?/gi, '')
                  .replace(/font-variant-caps:[^;]+;?/gi, '')
                  .replace(/font-variant-ligatures:[^;]+;?/gi, '')
                el.setAttribute('style', cleaned)
              })
            },
            'contentChanged': () => {
              const html = this.controller.html.get()
              const options2 = JSON.parse(JSON.stringify(this.options2))
              options2[this.elementId][this.id] = html
              this.callEvent({ methodName: 'setOptions2', eventData: options2, target: 'Design' })
            },
            'destroy': () => {}
          }
        })

        // Iterate Sections
        let elementFound = false
        this.sections2.every((section) => {
          if (section.id === this.elementId) {
            elementFound = true
            this.callEvent({
              methodName: 'setActiveObject',
              eventData: { id: this.elementId, type: 'section' },
              target: 'Design'
            })
            this.callEvent({ methodName: 'setDefaultEditorModal', target: 'Design' })
          }
          return !elementFound
        })
        if (!elementFound) {
          // Iterate Elements
          this.sections2.every((section) => {
            section.rows.every((row) => {
              row.columns.every((column) => {
                column.elements.every((element) => {
                  if (element.id === this.elementId) {
                    elementFound = true
                    this.callEvent({
                      methodName: 'setActiveObject',
                      eventData: { sectionId: section.id, rowId: row.id, columnId: column.id, id: element.id, key: element.key, type: 'element' },
                      target: 'Design'
                    })
                    this.callEvent({ methodName: 'setDefaultEditorModal', target: 'Design' })
                  }
                  return !elementFound
                })
                return !elementFound
              })
              return !elementFound
            })
            return !elementFound
          })
        }
      } else {
        // Already Initiated
        this.controller.toolbar.show()
      }
    },
    manageTextEditors(e) {
      if (
        this.showEditor &&
        this.controller &&
        this.controller.toolbar &&
        this.textEditorObject &&
        !this.isSelfEditorDescendant(e.target)
      ) {
        this.showEditor = false
        if (!this.isEditorDescendant(e.target) && this.wrapElement) {
          this.wrapElement.classList.remove('active')
        }
        this.controller.toolbar.hide()
      }
    },
    isEditorDescendant(child) {
      return !!getParentDOM(child, 'custom-textEditors')
    },
    isSelfEditorDescendant(child) {
      return this.textEditorObject.id === child.id || this.textEditorObject.contains(child)
    }
  }
}
</script>